import React, { useEffect, useMemo, useState } from "react";
import { FcGoogle } from "react-icons/fc";
import { HiArrowLeft } from "react-icons/hi";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import PageHero from "../components/PageHero";
import heroImage from "../assets/zeamays.png";
import { supabase } from "../supabaseClient";
import {
  clearAuthRedirectPath,
  readAuthRedirectPath,
  saveAuthRedirectPath,
} from "../utils/authRedirect";

const AUTH_SURFACE =
  "rounded-[2rem] border border-white/70 bg-white/85 p-6 shadow-[0_24px_80px_rgba(15,23,42,0.10)] backdrop-blur xl:p-8";
const BUTTON_BASE =
  "inline-flex w-full items-center justify-center gap-3 rounded-full px-5 py-3.5 text-sm font-semibold transition duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60";

const buildAuthRedirectTarget = () => {
  const redirectPath = readAuthRedirectPath() || "/";
  const normalizedPath = redirectPath.startsWith("/") ? redirectPath : "/";
  return `${window.location.origin}/login?next=${encodeURIComponent(normalizedPath)}`;
};

const Auth = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isSignUpPage = location.pathname === "/signup";
  const [mode, setMode] = useState("choice");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [sentEmail, setSentEmail] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const nextPath = useMemo(() => {
    const stateTarget = location.state?.from
      ? `${location.state.from.pathname || ""}${location.state.from.search || ""}`
      : "";
    const queryTarget = new URLSearchParams(location.search).get("next");
    return stateTarget || queryTarget || readAuthRedirectPath() || "/";
  }, [location.search, location.state]);
  const authRedirectTarget = location.state?.checkoutIntent === "login" ? "/cart?checkout=1" : nextPath;

  const pageTitle = isSignUpPage ? "Create your account" : "Sign in to continue";
  const pageDescription = isSignUpPage
    ? "Start with secure access for checkout, orders, and your saved profile."
    : "Fast, secure access for checkout, orders, and your saved profile.";
  const footerPrompt = isSignUpPage ? "Already have an account?" : "Don't have an account?";
  const footerLinkLabel = isSignUpPage ? "Sign in" : "Sign up";
  const footerLinkTarget = isSignUpPage ? "/login" : "/signup";

  useEffect(() => {
    saveAuthRedirectPath(authRedirectTarget);
  }, [authRedirectTarget]);

  useEffect(() => {
    if (!loading && user) {
      const redirectTarget = readAuthRedirectPath() || authRedirectTarget || "/";
      clearAuthRedirectPath();
      navigate(redirectTarget, { replace: true });
    }
  }, [authRedirectTarget, loading, navigate, user]);

  const handleGoogleAuth = async () => {
    setGoogleLoading(true);
    setErrorMessage("");
    setStatusMessage("");

    try {
      saveAuthRedirectPath(authRedirectTarget);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: buildAuthRedirectTarget(),
        },
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      setErrorMessage(error.message || "Unable to continue with Google.");
      setGoogleLoading(false);
    }
  };

  const handleEmailSubmit = async (event) => {
    event.preventDefault();

    if (!email.trim()) {
      setErrorMessage("Enter your email address.");
      return;
    }

    setSubmitting(true);
    setErrorMessage("");
    setStatusMessage("");

    try {
      saveAuthRedirectPath(authRedirectTarget);

      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: {
          emailRedirectTo: buildAuthRedirectTarget(),
        },
      });

      if (error) {
        throw error;
      }

      setSentEmail(email.trim());
      setMode("sent");
      setStatusMessage("Magic link sent successfully.");
    } catch (error) {
      setErrorMessage(error.message || "Unable to send sign-in link.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!sentEmail) {
      return;
    }

    setSubmitting(true);
    setErrorMessage("");
    setStatusMessage("");

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: sentEmail,
        options: {
          emailRedirectTo: buildAuthRedirectTarget(),
        },
      });

      if (error) {
        throw error;
      }

      setStatusMessage("A fresh sign-in link is on its way.");
    } catch (error) {
      setErrorMessage(error.message || "Unable to resend sign-in link.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!loading && user) {
    return <Navigate to={authRedirectTarget || "/"} replace />;
  }

  return (
    <div className="min-h-screen bg-[#f7fbef]">
      <PageHero
        eyebrow={isSignUpPage ? "Create your account" : "Secure access"}
        title={pageTitle}
        description={pageDescription}
        image={heroImage}
        imageAlt="Zea Mays account access"
        primaryAction={{ href: "#auth-form", label: isSignUpPage ? "Start sign up" : "Sign in now" }}
        secondaryAction={{ to: "/products", label: "Browse Products" }}
        stats={[
          { label: "Checkout", value: "Fast access" },
          { label: "Orders", value: "Saved here" },
          { label: "Profile", value: "Stay updated" },
          { label: "Security", value: "Protected" },
        ]}
        compact
      />

      <div className="px-4 py-12 sm:px-6">
        <div id="auth-form" className="mx-auto flex max-w-md items-center justify-center">
          <div className={`${AUTH_SURFACE} w-full`}>
            <div className="text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.32em] text-amber-700">
                {isSignUpPage ? "Sign up" : "Sign in"}
              </p>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 sm:text-[2rem]">
                {pageTitle}
              </h2>
              <p className="mt-3 text-sm leading-7 text-slate-600">
                {pageDescription}
              </p>
            </div>

            <div className="mt-8 space-y-3">
              {mode === "choice" ? (
                <>
                  <button
                    type="button"
                    onClick={handleGoogleAuth}
                    disabled={loading || googleLoading || submitting}
                    className={`${BUTTON_BASE} border border-slate-200 bg-white text-slate-900 hover:border-slate-300 hover:bg-slate-50`}
                  >
                    <FcGoogle className="text-xl" />
                    <span>
                      {loading ? "Checking session..." : googleLoading ? "Redirecting..." : "Continue with Google"}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setMode("email");
                      setErrorMessage("");
                      setStatusMessage("");
                    }}
                    disabled={loading || googleLoading || submitting}
                    className={`${BUTTON_BASE} bg-slate-950 text-white hover:bg-slate-800`}
                  >
                    Continue with Email
                  </button>
                </>
              ) : null}

              {mode === "email" ? (
                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <button
                    type="button"
                    onClick={() => {
                      setMode("choice");
                      setErrorMessage("");
                      setStatusMessage("");
                    }}
                    className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition hover:text-slate-800"
                  >
                    <HiArrowLeft className="text-base" />
                    Back
                  </button>

                  <div>
                    <label htmlFor="email" className="mb-2 block text-sm font-medium text-slate-700">
                      Email address
                    </label>
                    <input
                      id="email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder="you@example.com"
                      className="w-full rounded-[1.4rem] border border-slate-200 bg-white px-4 py-3.5 text-slate-900 outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100"
                      aria-describedby={errorMessage ? "auth-email-error" : undefined}
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting || googleLoading}
                    className={`${BUTTON_BASE} bg-slate-950 text-white hover:bg-slate-800`}
                  >
                    {submitting
                      ? "Sending secure link..."
                      : isSignUpPage
                        ? "Send secure sign-up link"
                        : "Send secure sign-in link"}
                  </button>
                </form>
              ) : null}

              {mode === "sent" ? (
                <div className="space-y-4 text-center">
                  <div className="rounded-[1.6rem] bg-emerald-50 px-5 py-5">
                    <p className="text-lg font-semibold text-slate-950">
                      Check your email for a secure sign-in link
                    </p>
                    <p className="mt-2 text-sm leading-7 text-slate-600">
                      We sent a magic link to{" "}
                      <span className="font-medium text-slate-900">{sentEmail}</span>.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={submitting}
                    className={`${BUTTON_BASE} border border-slate-200 bg-white text-slate-900 hover:border-slate-300 hover:bg-slate-50`}
                  >
                    {submitting ? "Resending..." : "Resend email"}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setMode("email");
                      setErrorMessage("");
                      setStatusMessage("");
                    }}
                    className="text-sm font-medium text-slate-500 transition hover:text-slate-800"
                  >
                    Use a different email
                  </button>
                </div>
              ) : null}
            </div>

            {errorMessage ? (
              <p
                id="auth-email-error"
                className="mt-4 rounded-[1.4rem] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                {errorMessage}
              </p>
            ) : null}

            {statusMessage ? (
              <p className="mt-4 rounded-[1.4rem] border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                {statusMessage}
              </p>
            ) : null}

            <p className="mt-6 text-center text-sm text-slate-500">
              {footerPrompt}{" "}
              <Link
                to={footerLinkTarget}
                state={
                  isSignUpPage
                    ? { ...location.state, from: { pathname: "/checkout" }, checkoutIntent: "login" }
                    : location.state
                }
                className="font-semibold text-slate-900 transition hover:text-amber-700"
              >
                {footerLinkLabel}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
